import React from "react";
import { Navigate } from "react-router-dom";
import { connect } from "react-redux";
import { firebaseConnect } from "react-redux-firebase";
import { get } from "lodash";
import {
  getSetupCardLockState,
  hasWebsitePublished,
  isCharityFirebasePathLoaded,
  isOrganizationSetupComplete,
  SetupCardKey,
} from "../../../../../../utils/onboarding/dashboardSequencing";
import { ONBOARDING_DASHBOARD_PATH } from "../../../../../../utils/onboarding/stepNavigation";
import OnboardingDashboardLoader from "../onboardingDashboardLoader";

type GateKind = "onboardingRequired" | "organizationComplete" | "mmsUnlocked";

interface Props {
  gate: GateKind;
  children: React.ReactElement;
  charityId: string;
  dataLoaded: boolean;
  onboardingRequired: boolean;
  organizationComplete: boolean;
  published: boolean;
}

const DashboardGateInner: React.FC<Props> = ({
  gate,
  children,
  dataLoaded,
  onboardingRequired,
  organizationComplete,
  published,
}) => {
  if (!dataLoaded) {
    return <OnboardingDashboardLoader />;
  }

  if (gate === "onboardingRequired" && !onboardingRequired) {
    return <Navigate to="/contacts" replace />;
  }

  if (gate === "organizationComplete") {
    const lock = getSetupCardLockState(
      "givespark" as SetupCardKey,
      organizationComplete,
      published
    );
    if (lock.locked) {
      return <Navigate to={ONBOARDING_DASHBOARD_PATH} replace />;
    }
  }

  if (gate === "mmsUnlocked") {
    const lock = getSetupCardLockState("mms", organizationComplete, published);
    if (lock.locked) {
      return <Navigate to={ONBOARDING_DASHBOARD_PATH} replace />;
    }
  }

  return children;
};

const mapStateToProps = (state: any) => {
  const { user, firebase } = state;
  const charityId = user.currentCharity?.id || "";

  const onboardingRequiredLoaded = isCharityFirebasePathLoaded(
    firebase,
    charityId,
    "onboardingRequired"
  );

  const organizationLoaded = isCharityFirebasePathLoaded(
    firebase,
    charityId,
    "charitySetup/organization"
  );

  const brandLoaded = isCharityFirebasePathLoaded(
    firebase,
    charityId,
    "charitySetup/brandSetup"
  );

  const organizationData = organizationLoaded
    ? get(firebase, `data.${charityId}.charitySetup.organization`, null)
    : null;

  const brandSetupData = brandLoaded
    ? get(firebase, `data.${charityId}.charitySetup.brandSetup`, null)
    : null;

  const dataLoaded =
    onboardingRequiredLoaded && organizationLoaded && brandLoaded;

  return {
    charityId,
    dataLoaded,
    onboardingRequired: onboardingRequiredLoaded
      ? get(firebase, `data.${charityId}.onboardingRequired`) === true
      : false,
    organizationComplete: isOrganizationSetupComplete(
      organizationData,
      organizationLoaded
    ),
    published: hasWebsitePublished(brandSetupData),
  };
};

const DashboardGate = connect(mapStateToProps)(
  firebaseConnect((_props: any, store: any) => {
    const charityId = store.getState().user.currentCharity?.id;
    if (!charityId) {
      return [];
    }
    return [
      `${charityId}/onboardingRequired`,
      `${charityId}/charitySetup/organization`,
      `${charityId}/charitySetup/brandSetup`,
    ];
  })(DashboardGateInner)
);

export default DashboardGate;
